import {
  ClientProjectName,
  ClientProjectNumber,
  ProjectName,
  ProjectFilename,
} from './instances/project'
import {
  AsphaltLotDate,
  AsphaltLotId,
  AsphaltMixDesign,
  AsphaltMixGrade,
  AsphaltPlacementType,
  AsphaltShortName,
  AsphaltFilename,
} from './instances/asphalt'
import {
  ConcreteClassification,
  ConcreteClientMixDesign,
  ConcreteFieldNumber,
  ConcreteFieldNumbers,
  ConcreteMixDesign,
  ConcretePlacementDate,
  ConcretePlacementId,
  ConcretePlacementIds,
  ConcretePlacementType,
  ConcreteShortName,
  ConcreteTestSetId,
  ConcreteTestSubId,
  ConcreteTestSubIds,
  ConcreteFilename,
} from './instances/concrete'
import {
  DensityLotClassification,
  DensityLotDate,
  DensityLotId,
  DensityLotMaterialSection,
  DensityLotMaterialSource,
  DensityLotShortName,
  DensityFilename,
} from './instances/density'
import {
  DailyControlPointDate,
  DailyControlPointId,
  DailyInspectionDate,
  DailyInspectionId,
  DailyInspectionShortName,
  DailyInspectionWorkType,
  DailyPunchListDate,
  DailyPunchListId,
  DailyInspectionFilename,
} from './instances/dailyInspection'
import {
  PhotoSequenceNumber,
  ClasslessFilename,
  Tags,
  TagCategories,
} from './instances/classless'
import {
  CASCreatedDate,
  CASDispositionDate,
  CASPackageName,
  CASPackageNumber,
  CASSubtype,
  CASType,
  CASFilename,
} from './instances/cas'
import { NamedComplexEnum } from '../../utils/complexEnum/NamedComplexEnum'
import { FilenameTemplateItem as ClassedItem } from './classes/FilenameTemplateItem'

export type FilenameTemplateItem =
  | ProjectFilename
  | AsphaltFilename
  | ConcreteFilename
  | DensityFilename
  | DailyInspectionFilename
  | ClasslessFilename
  | CASFilename
type ID = FilenameTemplateItem['id']
type Name = FilenameTemplateItem['name']

class FilenameTemplateEnum extends NamedComplexEnum<
  'FilenameTemplate',
  FilenameTemplateItem,
  Name,
  ID
> {
  readonly typename = 'FilenameTemplate'
  /**
   * all possible properties
   */
  get properties() {
    return [...new Set(this.map(({ property }) => property))]
  }
  /**
   * all possible classType values for items with a classType
   */
  get classTypes() {
    return [
      ...this.reduce((classTypes, value) => {
        if (value instanceof ClassedItem) classTypes.add(value.classType)
        return classTypes
      }, new Set<Exclude<FilenameTemplateItem, ClasslessFilename>['classType']>()),
    ]
  }
  /**
   * tests whether the supplied string is illegal
   *
   * @param string
   */
  isIllegal(item: string) {
    return item === '' || this.illegal.regex.test(item)
  }
  /**
   * an object containing illegal characters as an array and regex
   */
  illegal = {
    regex: new RegExp(/[.,<>:"/\\|?*\x00-\x1F]/g),
    characters: ['.', ',', '<', '>', ':', ' "', '/', '\\', '?', '|', '*'],
  } as const
  get values() {
    return [
      this.ClientProjectName,
      this.ClientProjectNumber,
      this.ProjectName,
      this.AsphaltLotDate,
      this.AsphaltLotId,
      this.AsphaltMixDesign,
      this.AsphaltMixGrade,
      this.AsphaltPlacementType,
      this.AsphaltShortName,
      this.ConcreteClassification,
      this.ConcreteClientMixDesign,
      this.ConcreteFieldNumber,
      this.ConcreteFieldNumbers,
      this.ConcreteMixDesign,
      this.ConcretePlacementDate,
      this.ConcretePlacementId,
      this.ConcretePlacementIds,
      this.ConcretePlacementType,
      this.ConcreteShortName,
      this.ConcreteTestSetId,
      this.ConcreteTestSubId,
      this.ConcreteTestSubIds,
      this.DensityLotClassification,
      this.DensityLotDate,
      this.DensityLotId,
      this.DensityLotMaterialSection,
      this.DensityLotMaterialSource,
      this.DensityLotShortName,
      this.DailyControlPointDate,
      this.DailyControlPointId,
      this.DailyInspectionDate,
      this.DailyInspectionId,
      this.DailyInspectionShortName,
      this.DailyInspectionWorkType,
      this.DailyPunchListDate,
      this.DailyPunchListId,
      this.PhotoSequenceNumber,
      this.CASCreatedDate,
      this.CASDispositionDate,
      this.CASPackageName,
      this.CASPackageNumber,
      this.CASSubtype,
      this.CASType,
      this.Tags,
      this.TagCategories,
    ]
  }
  readonly Tags = Tags
  readonly TagCategories = TagCategories
  readonly ClientProjectName = ClientProjectName
  readonly ClientProjectNumber = ClientProjectNumber
  readonly ProjectName = ProjectName
  readonly AsphaltLotDate = AsphaltLotDate
  readonly AsphaltLotId = AsphaltLotId
  readonly AsphaltMixDesign = AsphaltMixDesign
  readonly AsphaltMixGrade = AsphaltMixGrade
  readonly AsphaltPlacementType = AsphaltPlacementType
  readonly AsphaltShortName = AsphaltShortName
  readonly ConcreteClassification = ConcreteClassification
  readonly ConcreteClientMixDesign = ConcreteClientMixDesign
  readonly ConcreteFieldNumber = ConcreteFieldNumber
  readonly ConcreteFieldNumbers = ConcreteFieldNumbers
  readonly ConcreteMixDesign = ConcreteMixDesign
  readonly ConcretePlacementDate = ConcretePlacementDate
  readonly ConcretePlacementId = ConcretePlacementId
  readonly ConcretePlacementIds = ConcretePlacementIds
  readonly ConcretePlacementType = ConcretePlacementType
  readonly ConcreteShortName = ConcreteShortName
  readonly ConcreteTestSetId = ConcreteTestSetId
  readonly ConcreteTestSubId = ConcreteTestSubId
  readonly ConcreteTestSubIds = ConcreteTestSubIds
  readonly DensityLotClassification = DensityLotClassification
  readonly DensityLotDate = DensityLotDate
  readonly DensityLotId = DensityLotId
  readonly DensityLotMaterialSection = DensityLotMaterialSection
  readonly DensityLotMaterialSource = DensityLotMaterialSource
  readonly DensityLotShortName = DensityLotShortName
  readonly DailyControlPointDate = DailyControlPointDate
  readonly DailyControlPointId = DailyControlPointId
  readonly DailyInspectionDate = DailyInspectionDate
  readonly DailyInspectionId = DailyInspectionId
  readonly DailyInspectionShortName = DailyInspectionShortName
  readonly DailyInspectionWorkType = DailyInspectionWorkType
  readonly DailyPunchListDate = DailyPunchListDate
  readonly DailyPunchListId = DailyPunchListId
  readonly PhotoSequenceNumber = PhotoSequenceNumber
  readonly CASCreatedDate = CASCreatedDate
  readonly CASDispositionDate = CASDispositionDate
  readonly CASPackageName = CASPackageName
  readonly CASPackageNumber = CASPackageNumber
  readonly CASSubtype = CASSubtype
  readonly CASType = CASType
}

export const FilenameTemplate = new FilenameTemplateEnum()
